import { getAccessToken, accessTokenInterceptor } from '@teko/auth';
import Api from '@teko/api';
import { getError } from './error.es.js';

const LS_CART_TOKEN = 'cart_token';
const cartTokenHeader = 'x-cart-token';
const getCartToken = (localStorageKey) => localStorage.getItem(localStorageKey);
const setCartToken = (token, localStorageKey = LS_CART_TOKEN) => localStorage.setItem(localStorageKey, token);
const defaultCartValue = {
    id: '',
    grandTotal: 0,
    orderCoupons: {
        applicableCoupons: [],
        inapplicableCoupons: [],
    },
    orderLines: [],
    promotions: [],
    shippingFee: 0,
    subTotal: 0,
    totalDiscount: 0,
    eVouchers: [],
    allowToRequestOnDemandDiscount: false,
};
const parseJWT = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
            .join(''));
        return JSON.parse(jsonPayload)['cid'];
    }
    catch (_a) {
        return null;
    }
};
class CartService {
    constructor(cartApiUrl, localStorageKey = LS_CART_TOKEN, language) {
        this.getCart = async (params) => {
            const cartToken = getCartToken(this.localStorageKey);
            const accessToken = getAccessToken();
            return !cartToken && !accessToken
                ? Promise.resolve(defaultCartValue)
                : this.cartApi
                    .get('/api/v1/cart', { params })
                    .then(({ data }) => data.result);
        };
        this.addToCart = async (params, body) => {
            const { data, headers } = await this.cartApi.post('api/v1/cart/add-to-cart', body, { params });
            const token = headers[cartTokenHeader];
            if (token && token !== 'undefined') {
                localStorage.setItem(this.localStorageKey, token);
            }
            return Object.assign(Object.assign({}, data.result), { cartId: parseJWT(token) });
        };
        this.applyCoupon = async (params, body) => {
            const { data } = await this.cartApi.post('api/v1/cart/apply-coupon', body, {
                params,
            });
            return data.result;
        };
        this.removeCoupon = (params, body) => {
            return this.cartApi.delete('api/v1/cart/clear-coupon', body, { params });
        };
        this.clearCart = (params) => {
            return this.cartApi.delete('api/v1/cart', null, { params });
        };
        this.updateOrderLine = async (params, id, body) => {
            return this.cartApi
                .put(`api/v1/cart/order-lines/${id}`, body, {
                params: Object.assign(Object.assign({}, params), { graceful: true }),
            })
                .then(({ data }) => Promise.resolve(data.result));
        };
        this.updateSellerItems = (params, sellerId, selected) => {
            return this.cartApi.put('api/v1/cart/order-items', { selected }, { params: Object.assign(Object.assign({}, params), { sellerId }) });
        };
        this.removeOrderLine = async (params, id) => {
            return await this.cartApi.delete(`api/v1/cart/order-lines/${id}`, null, {
                params,
            });
        };
        this.setShippingAddress = (params, selectedAddress) => {
            const infoSetShippingAddr = Object.assign(Object.assign({ addressId: selectedAddress.addressId || selectedAddress.id, wardCode: (selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.wardCode) || '', districtCode: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.districtCode, provinceCode: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.provinceCode, wardName: (selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.wardName) || '', districtName: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.districtName, provinceName: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.provinceName, address: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.address, name: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.name }, ((selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.email) && { email: selectedAddress.email })), { telephone: selectedAddress === null || selectedAddress === void 0 ? void 0 : selectedAddress.telephone, addressNote: '' });
            return this.cartApi.post(`api/v1/cart/shipping-address`, infoSetShippingAddr, { params });
        };
        this.getPaymentMethod = async (params, orderCode) => {
            const { data } = await this.cartApi.get('api/v1/cart/payment-methods-v2', {
                params: Object.assign(Object.assign({}, params), (orderCode ? { orderCode } : {})),
            });
            return data.result;
        };
        this.checkout = async (params, body) => {
            var _a, _b, _c, _d;
            const { data } = await this.cartApi.post('api/v1/cart/checkout', body, {
                params,
            });
            return Object.assign(Object.assign({}, data.result), { orderId: ((_a = data.result) === null || _a === void 0 ? void 0 : _a.orderIdString) || ((_b = data.result) === null || _b === void 0 ? void 0 : _b.orderId), id: ((_c = data.result) === null || _c === void 0 ? void 0 : _c.orderIdString) || ((_d = data.result) === null || _d === void 0 ? void 0 : _d.orderId), get remainPayment() {
                    return data.result.grandTotal - data.result.totalPaid;
                } });
        };
        this.getOrderByCode = async (params, code) => {
            const { data } = await this.cartApi.get(`/api/v1/orders/${code}`, {
                params,
            });
            const order = data.result;
            return Object.assign(Object.assign({}, order), { orderId: (order === null || order === void 0 ? void 0 : order.orderIdString) || (order === null || order === void 0 ? void 0 : order.id), id: (order === null || order === void 0 ? void 0 : order.orderIdString) || (order === null || order === void 0 ? void 0 : order.id), grandTotal: parseInt(order.grandTotal), get remainPayment() {
                    var _a;
                    const prevPayment = ((_a = order === null || order === void 0 ? void 0 : order.payments) === null || _a === void 0 ? void 0 : _a.reduce((amount, currentPayment) => amount + currentPayment.amount, 0)) || 0;
                    return order.grandTotal - prevPayment;
                } });
        };
        this.getApplicableServices = async (params) => {
            const { data } = await this.cartApi.get('api/v1/cart/applicable-services', {
                params,
            });
            return data.result;
        };
        this.findApplicableServices = async (params, body) => {
            const { data } = await this.cartApi.post('/api/v1/cart/applicable-services', body, { params });
            return data.result;
        };
        this.getDeliveryOptions = async (params, body) => {
            const { data } = await this.cartApi.post('/api/v1/cart/delivery-options', body, { params });
            return data.result;
        };
        this.applyServiceToCart = async (params, body) => {
            const { data } = await this.cartApi.put('/api/v1/cart/apply-service', body, { params });
            return data.result;
        };
        this.applyPaymentMethodToCard = async (params, body) => {
            const { data } = await this.cartApi.put('/api/v1/cart/apply-payment-method', body, { params });
            return data.result;
        };
        this.calculatePrice = async (params, body) => {
            var _a;
            const { data } = await this.cartApi.post('/api/v2/products/calculate-price', body, { params });
            return (_a = data.data) === null || _a === void 0 ? void 0 : _a.price;
        };
        this.cartApi = new Api(cartApiUrl, getError);
        this.localStorageKey = localStorageKey;
        this.cartApi.addInterceptor(accessTokenInterceptor);
        this.cartApi.addInterceptor((request) => {
            const cartToken = getCartToken(localStorageKey);
            if (cartToken) {
                request.headers.set(cartTokenHeader, cartToken);
            }
        });
        this.cartApi.addInterceptor((request) => {
            request.headers.set('Accept-Language', language || '*');
        });
    }
}

export { CartService, defaultCartValue, parseJWT, setCartToken };
